import { useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  jobsApi,
  type JobDetailDto,
  type JobItemDto,
  type UpdateJobItemRequest,
  type JobDeliveryDto,
} from "@/lib/jobs";
import { JobDeliveryEditor } from "./components/JobDeliveryEditor";
import { DispatchModal } from "./components/DispatchModal";
import { MarkReadyConfirmModal } from "./components/MarkReadyConfirmModal";
import { jobApprovals, jobApprovalKeys } from "@/lib/job-approvals";
import { jobFiles, jobFileKeys } from "@/lib/job-files";
import { jobEmails, jobEmailKeys } from "@/lib/job-emails";
import { JobStatusBadge } from "@/components/job-status-badge";
import { TransitionMenu } from "@/components/transition-menu";
import { JobItemsGrid, type JobItemRow, blankRow, rowToRequest } from "@/components/job-items-grid";
import { JobEventList } from "@/components/job-event-list";
import { ApprovalList } from "@/components/approval-list";
import { JobFileGrid } from "@/components/job-file-grid";
import { JobEmailThread } from "@/components/job-email-thread";
import { InternalLinksPanel } from "@/components/internal-links-panel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { PageContainer } from "@/app/layouts/PageContainer";
import { usePermissions } from "@/hooks/use-permissions";

// ---------------------------------------------------------------------------
// Currency formatter
// ---------------------------------------------------------------------------

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

// ---------------------------------------------------------------------------
// OverviewTab
// ---------------------------------------------------------------------------

function OverviewRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value ?? "—"}</dd>
    </div>
  );
}

function OverviewTab({
  job,
  onDeliverySaved,
}: {
  job: JobDetailDto;
  onDeliverySaved: (next: JobDeliveryDto) => void;
}) {
  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
        <OverviewRow label="Owner" value={job.ownerEmployeeName} />
        <OverviewRow
          label="Branch"
          value={job.branchId != null ? `Branch #${job.branchId}` : null}
        />
        <OverviewRow label="Description" value={job.description} />
        <OverviewRow label="Customer reference" value={job.customerReference} />
        <OverviewRow label="PO number" value={job.purchaseOrderNumber} />
        <OverviewRow
          label="Expected dispatch"
          value={
            job.expectedDispatchDate
              ? new Date(job.expectedDispatchDate).toLocaleDateString("en-AU")
              : null
          }
        />
        <OverviewRow
          label="Total (ex GST)"
          value={job.totalExGst != null ? fmt.format(job.totalExGst) : null}
        />
        <OverviewRow
          label="GST"
          value={job.gst != null ? fmt.format(job.gst) : null}
        />
        <OverviewRow
          label="Total (inc GST)"
          value={job.totalIncGst != null ? fmt.format(job.totalIncGst) : null}
        />
        <OverviewRow
          label="Status entered"
          value={new Date(job.currentStatusEnteredUtc).toLocaleString("en-AU")}
        />
        <OverviewRow
          label="Converted from quote"
          value={job.convertedFromQuoteId != null ? `#${job.convertedFromQuoteId}` : null}
        />
      </dl>

      {job.delivery != null && (
        <JobDeliveryEditor
          jobId={job.jobId}
          rowVersionBase64={job.rowVersionBase64 ?? ''}
          delivery={job.delivery}
          onSaved={onDeliverySaved}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ItemsTab
// ---------------------------------------------------------------------------

function itemDtoToRow(item: JobItemDto): JobItemRow {
  return {
    _key: `srv-${item.jobItemId}`,
    sortOrder: String(item.sortOrder),
    description: item.description,
    quantity: String(item.quantity),
    unitPriceExGst: String(item.unitPriceExGst),
    gstRate: String(item.gstRate),
  };
}

function ItemsTab({
  jobId,
  serverItems,
  onSaved,
}: {
  jobId: number;
  serverItems: JobItemDto[];
  onSaved: () => void;
}) {
  const [rows, setRows] = useState<JobItemRow[]>(() => serverItems.map(itemDtoToRow));
  const [saving, setSaving] = useState(false);

  // Keep rows in sync when serverItems changes (after invalidation)
  const serverKeys = serverItems.map((i) => i.jobItemId).join(",");
  useState(() => {
    // intentional: initialise only; subsequent refreshes handled by key change below
  });

  // Re-derive rows from server whenever serverItems reference changes.
  // We use a separate reconcile approach: if serverKeys changed, reset rows.
  // Track via ref-like state comparison.
  const [prevServerKeys, setPrevServerKeys] = useState(serverKeys);
  if (serverKeys !== prevServerKeys) {
    setPrevServerKeys(serverKeys);
    setRows(serverItems.map(itemDtoToRow));
  }

  async function handleSave() {
    setSaving(true);
    try {
      const existingIds = new Set(serverItems.map((i) => i.jobItemId));

      // Determine which server items were removed
      const rowServerIds = new Set(
        rows
          .filter((r) => r._key.startsWith("srv-"))
          .map((r) => Number(r._key.replace("srv-", "")))
      );
      for (const item of serverItems) {
        if (!rowServerIds.has(item.jobItemId)) {
          await jobsApi.deleteItem(jobId, item.jobItemId);
        }
      }

      for (const row of rows) {
        const req = rowToRequest(row);
        if (!req) continue;

        if (row._key.startsWith("srv-")) {
          const itemId = Number(row._key.replace("srv-", ""));
          if (existingIds.has(itemId)) {
            const serverItem = serverItems.find((i) => i.jobItemId === itemId);
            if (!serverItem) continue;
            const updateReq: UpdateJobItemRequest = {
              sortOrder: req.sortOrder ?? 0,
              description: req.description,
              quantity: req.quantity,
              unitPriceExGst: req.unitPriceExGst,
              gstRate: req.gstRate ?? 0.1,
              rowVersionBase64: serverItem.rowVersionBase64 ?? "",
            };
            await jobsApi.updateItem(jobId, itemId, updateReq);
          }
        } else {
          // New row
          await jobsApi.createItem(jobId, req);
        }
      }

      toast.success("Items saved.");
      onSaved();
    } catch (err) {
      toast.error(`Save failed: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-3">
      <JobItemsGrid items={rows} onChange={setRows} editable />
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save items"}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// NotesTab
// ---------------------------------------------------------------------------

function NoteField({
  jobId,
  label,
  fetcher,
  saver,
}: {
  jobId: number;
  label: string;
  fetcher: () => Promise<{ note: string | null; rowVersionBase64: string | null }>;
  saver: (body: { note: string | null; rowVersionBase64: string }) => Promise<void>;
}) {
  const queryKey = [`job-notes-${label}`, jobId];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: fetcher,
  });

  const qc = useQueryClient();
  const [localText, setLocalText] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const currentText = localText ?? data?.note ?? "";

  async function handleBlur() {
    if (data == null) return;
    const next = localText;
    if (next === null) return; // nothing changed
    if (next === (data.note ?? "")) return; // unchanged

    setSaving(true);
    try {
      await saver({ note: next, rowVersionBase64: data.rowVersionBase64 ?? "" });
      toast.success(`${label} saved.`);
      void qc.invalidateQueries({ queryKey });
      setLocalText(null); // reset local draft; fresh data from query
    } catch (err) {
      toast.error(`Save failed: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setSaving(false);
    }
  }

  if (isLoading) {
    return (
      <div>
        <label className="block text-sm font-medium mb-1">{label}</label>
        <div className="h-24 rounded-md border bg-muted animate-pulse" />
      </div>
    );
  }

  return (
    <div>
      <label className="block text-sm font-medium mb-1">
        {label}
        {saving && <span className="ml-2 text-xs text-muted-foreground">Saving…</span>}
      </label>
      <textarea
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        rows={5}
        value={currentText}
        onChange={(e) => setLocalText(e.target.value)}
        onBlur={handleBlur}
        disabled={saving}
        aria-label={label}
      />
    </div>
  );
}

function NotesTab({ jobId }: { jobId: number }) {
  return (
    <div className="space-y-6">
      <NoteField
        jobId={jobId}
        label="Job notes"
        fetcher={() => jobsApi.getNotes(jobId)}
        saver={(body) => jobsApi.putNotes(jobId, body)}
      />
      <NoteField
        jobId={jobId}
        label="Extra notes"
        fetcher={() => jobsApi.getExtraNotes(jobId)}
        saver={(body) => jobsApi.putExtraNotes(jobId, body)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// ApprovalsTab
// ---------------------------------------------------------------------------

function ApprovalsTab({ jobId }: { jobId: number }) {
  const { has } = usePermissions();
  const canDecide = has("jobs:approve");
  const qc = useQueryClient();

  const { data: approvals = [], isLoading } = useQuery({
    queryKey: jobApprovalKeys.list(jobId),
    queryFn: () => jobApprovals.list(jobId),
  });

  const decide = useMutation({
    mutationFn: ({
      approvalId,
      decision,
      comment,
      rowVersionBase64,
    }: {
      approvalId: number;
      decision: "Approve" | "Reject";
      comment: string | undefined;
      rowVersionBase64: string;
    }) => jobApprovals.decide(jobId, approvalId, { decision, comment, rowVersionBase64 }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: jobApprovalKeys.list(jobId) });
      toast.success("Decision recorded.");
    },
    onError: (err: Error) => toast.error(`Decision failed: ${err.message}`),
  });

  const addComment = useMutation({
    mutationFn: ({ approvalId, text }: { approvalId: number; text: string }) =>
      jobApprovals.addComment(jobId, approvalId, text),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: jobApprovalKeys.comments(jobId, vars.approvalId),
      });
    },
    onError: (err: Error) => toast.error(`Comment failed: ${err.message}`),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-3">
      {!canDecide && (
        <p className="text-xs text-muted-foreground">You do not have permission to approve jobs.</p>
      )}
      <ApprovalList
        approvals={approvals}
        onDecide={(approvalId, decision, comment, rowVersionBase64) =>
          decide.mutate({ approvalId, decision, comment, rowVersionBase64 })
        }
        onAddComment={(approvalId, text) => addComment.mutate({ approvalId, text })}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// FilesTab
// ---------------------------------------------------------------------------

function FilesTab({ jobId }: { jobId: number }) {
  const { has } = usePermissions();
  const isAdmin = has("lookups:write") && has("job-files:write");
  const qc = useQueryClient();

  const { data: files = [], isLoading: filesLoading } = useQuery({
    queryKey: jobFileKeys.list(jobId),
    queryFn: () => jobFiles.list(jobId),
  });

  const { data: links = [], isLoading: linksLoading } = useQuery({
    queryKey: jobFileKeys.internalLinks(jobId),
    queryFn: () => jobFiles.internalLinks.list(jobId),
  });

  const upload = useMutation({
    mutationFn: (file: File) => jobFiles.upload(jobId, file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: jobFileKeys.list(jobId) });
      toast.success("File uploaded.");
    },
    onError: (err: Error) => toast.error(`Upload failed: ${err.message}`),
  });

  const uploadVersion = useMutation({
    mutationFn: ({ fileId, file }: { fileId: number; file: File }) =>
      jobFiles.uploadVersion(jobId, fileId, file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: jobFileKeys.list(jobId) });
      toast.success("New version uploaded.");
    },
    onError: (err: Error) => toast.error(`Upload version failed: ${err.message}`),
  });

  const removeFile = useMutation({
    mutationFn: (fileId: number) => jobFiles.remove(jobId, fileId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: jobFileKeys.list(jobId) });
      toast.success("File deleted.");
    },
    onError: (err: Error) => toast.error(`Delete failed: ${err.message}`),
  });

  const saveLink = useMutation({
    mutationFn: (req: { linkDisplayText: string; comment?: string; rowVersionBase64?: string }) => {
      if (req.rowVersionBase64) {
        // update — need an id; InternalLinksPanel passes rowVersionBase64 so we search for it
        const existing = links.find((l) => l.rowVersionBase64 === req.rowVersionBase64);
        if (existing) {
          return jobFiles.internalLinks.update(jobId, existing.jobInternalFileLinkId, {
            linkDisplayText: req.linkDisplayText,
            comment: req.comment,
            rowVersionBase64: req.rowVersionBase64,
          });
        }
      }
      return jobFiles.internalLinks.create(jobId, {
        linkDisplayText: req.linkDisplayText,
        comment: req.comment,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: jobFileKeys.internalLinks(jobId) });
      toast.success("Link saved.");
    },
    onError: (err: Error) => toast.error(`Save link failed: ${err.message}`),
  });

  const deleteLink = useMutation({
    mutationFn: ({ id, rowVersionBase64 }: { id: number; rowVersionBase64: string }) =>
      jobFiles.internalLinks.remove(jobId, id, rowVersionBase64),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: jobFileKeys.internalLinks(jobId) });
      toast.success("Link deleted.");
    },
    onError: (err: Error) => toast.error(`Delete link failed: ${err.message}`),
  });

  if (filesLoading || linksLoading)
    return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-6">
      <JobFileGrid
        jobId={jobId}
        files={files}
        onUpload={(file) => upload.mutate(file)}
        onUploadNewVersion={(fileId, file) => uploadVersion.mutate({ fileId, file })}
        onDelete={(fileId) => {
          if (isAdmin) removeFile.mutate(fileId);
          else toast.error("You do not have permission to delete files.");
        }}
      />
      <InternalLinksPanel
        jobId={jobId}
        links={links}
        onSave={(req) => saveLink.mutate(req)}
        onDelete={(id, rowVersionBase64) => deleteLink.mutate({ id, rowVersionBase64 })}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// EmailsTab
// ---------------------------------------------------------------------------

function EmailsTab({ jobId }: { jobId: number }) {
  const qc = useQueryClient();

  const { data: emails = [], isLoading } = useQuery({
    queryKey: jobEmailKeys.thread(jobId),
    queryFn: () => jobEmails.list(jobId),
  });

  const send = useMutation({
    mutationFn: (body: { toEmailAddress: string; subject: string; htmlBody: string }) =>
      jobEmails.send(jobId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: jobEmailKeys.thread(jobId) });
      toast.success("Email sent.");
    },
    onError: (err: Error) => toast.error(`Send failed: ${err.message}`),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <JobEmailThread
      jobId={jobId}
      emails={emails}
      onSend={(body) => send.mutate(body)}
    />
  );
}

// ---------------------------------------------------------------------------
// SkeletonDetail
// ---------------------------------------------------------------------------

function SkeletonDetail() {
  return (
    <PageContainer>
      <div className="animate-pulse space-y-4">
        <div className="h-8 w-64 rounded bg-muted" />
        <div className="h-4 w-48 rounded bg-muted" />
        <div className="h-64 rounded bg-muted" />
      </div>
    </PageContainer>
  );
}

// ---------------------------------------------------------------------------
// JobDetailPage
// ---------------------------------------------------------------------------

export function JobDetailPage() {
  const { id = "0" } = useParams();
  const jobId = Number(id);
  const qc = useQueryClient();
  const nav = useNavigate();

  const jobQuery = useQuery({
    queryKey: ["job", jobId],
    queryFn: () => jobsApi.get(jobId),
    enabled: !!jobId,
  });

  const itemsQuery = useQuery({
    queryKey: ["job-items", jobId],
    queryFn: () => jobsApi.items(jobId),
    enabled: !!jobId,
  });

  const eventsQuery = useQuery({
    queryKey: ["job-events", jobId],
    queryFn: () => jobsApi.events(jobId),
    enabled: !!jobId,
  });

  const transitionsQuery = useQuery({
    queryKey: ["job-transitions", jobId, jobQuery.data?.currentStatusCode],
    queryFn: () => jobsApi.legalTransitions(jobId),
    enabled: !!jobId && !!jobQuery.data,
  });

  const changeStatus = useMutation({
    mutationFn: (target: string) =>
      jobsApi.changeStatus(jobId, {
        targetStatusCode: target,
        rowVersionBase64: jobQuery.data!.rowVersionBase64 ?? "",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["job", jobId] });
      void qc.invalidateQueries({ queryKey: ["job-events", jobId] });
      void qc.invalidateQueries({ queryKey: ["job-transitions", jobId] });
      toast.success("Status changed.");
    },
    onError: (err: Error) => toast.error(`Status change failed: ${err.message}`),
  });

  const generateInvoice = useMutation({
    mutationFn: () => jobsApi.generateInvoice(jobId),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["job", jobId] });
      toast.success(`Invoice #${r.invoiceId} created.`);
      nav(`/invoices/${r.invoiceId}`);
    },
    onError: (err: Error) => toast.error(`Generate invoice failed: ${err.message}`),
  });

  const handleItemsSaved = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ["job-items", jobId] });
    void qc.invalidateQueries({ queryKey: ["job", jobId] });
  }, [qc, jobId]);

  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [markReadyOpen, setMarkReadyOpen] = useState(false);

  const printJobSheetAction = async () => {
    if (!jobQuery.data) return;
    try {
      const resp = await jobsApi.printJobSheet(jobQuery.data.jobId);
      window.open(resp.pdfUrl, '_blank');
    } catch (e) {
      toast.error(`Print Job Sheet failed: ${e instanceof Error ? e.message : 'Unknown error'}`);
    }
  };

  if (jobQuery.isLoading) return <SkeletonDetail />;
  if (jobQuery.isError || !jobQuery.data) {
    return (
      <PageContainer>
        <p className="p-6 text-sm text-muted-foreground">Job not found.</p>
      </PageContainer>
    );
  }

  const j = jobQuery.data;
  const canGenerate = j.currentStatusCode === "Completed";

  return (
    <PageContainer>
      {/* Header */}
      <header className="mb-6 flex flex-wrap items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h1 className="text-2xl font-semibold">{j.jobReferenceNo}</h1>
            {j.clientName && (
              <>
                <span className="text-muted-foreground">·</span>
                <span className="text-base text-muted-foreground">{j.clientName}</span>
              </>
            )}
            <span className="text-muted-foreground">·</span>
            <JobStatusBadge code={j.currentStatusCode} color={j.statusColor} />
          </div>
          {j.description && (
            <p className="text-sm text-muted-foreground truncate">{j.description}</p>
          )}
        </div>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_300px]">
        {/* Left pane: tabs */}
        <main>
          <Tabs defaultValue="overview">
            <TabsList className="flex-wrap h-auto gap-1">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="items">Items</TabsTrigger>
              <TabsTrigger value="notes">Notes</TabsTrigger>
              <TabsTrigger value="events">Events</TabsTrigger>
              <TabsTrigger value="approvals">Approvals</TabsTrigger>
              <TabsTrigger value="files">Files</TabsTrigger>
              <TabsTrigger value="emails">Emails</TabsTrigger>
              <TabsTrigger value="bom" disabled>
                BOM (Plan IV)
              </TabsTrigger>
              <TabsTrigger value="delivery" disabled>
                Delivery (Plan III)
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <OverviewTab
                job={j}
                onDeliverySaved={() => void qc.invalidateQueries({ queryKey: ["job", jobId] })}
              />
            </TabsContent>

            <TabsContent value="items">
              <ItemsTab
                jobId={jobId}
                serverItems={itemsQuery.data ?? []}
                onSaved={handleItemsSaved}
              />
            </TabsContent>

            <TabsContent value="notes">
              <NotesTab jobId={jobId} />
            </TabsContent>

            <TabsContent value="events">
              <JobEventList events={eventsQuery.data ?? []} />
            </TabsContent>

            <TabsContent value="approvals">
              <ApprovalsTab jobId={jobId} />
            </TabsContent>

            <TabsContent value="files">
              <FilesTab jobId={jobId} />
            </TabsContent>

            <TabsContent value="emails">
              <EmailsTab jobId={jobId} />
            </TabsContent>
          </Tabs>
        </main>

        {/* Right pane: actions */}
        <aside className="space-y-3">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Actions
          </h2>

          <TransitionMenu
            transitions={transitionsQuery.data ?? []}
            onSelect={(t) => changeStatus.mutate(t.targetStatusCode)}
            disabled={changeStatus.isPending}
          />

          {/* Plan III: production-track status-keyed action buttons */}
          {j.currentStatusCode === "InProgress" && (
            <Button className="w-full" onClick={() => setMarkReadyOpen(true)}>
              Mark Ready for Production
            </Button>
          )}
          {j.currentStatusCode === "ReadyForProduction" && (
            <>
              <Button className="w-full" onClick={() => changeStatus.mutate("InProduction")}>
                Start Production
              </Button>
              <Button variant="outline" className="w-full" onClick={() => changeStatus.mutate("OnHold")}>
                Hold
              </Button>
              <Button variant="ghost" className="w-full" onClick={() => changeStatus.mutate("Cancelled")}>
                Cancel
              </Button>
            </>
          )}
          {j.currentStatusCode === "InProduction" && (
            <>
              <Button className="w-full" onClick={() => changeStatus.mutate("Completed")}>
                Mark Completed
              </Button>
              <Button variant="outline" className="w-full" onClick={() => changeStatus.mutate("OnHold")}>
                Hold
              </Button>
              <Button variant="ghost" className="w-full" onClick={() => changeStatus.mutate("Cancelled")}>
                Cancel
              </Button>
            </>
          )}
          {j.currentStatusCode === "Completed" && (
            <>
              <Button className="w-full" onClick={() => setDispatchOpen(true)}>
                Mark Dispatched
              </Button>
              <Button variant="outline" className="w-full" onClick={() => setDispatchOpen(true)}>
                Print Delivery Docket
              </Button>
              <Button variant="outline" className="w-full" onClick={() => void printJobSheetAction()}>
                Print Job Sheet
              </Button>
            </>
          )}
          {j.currentStatusCode === "Dispatched" && (
            <>
              <Button variant="outline" className="w-full" onClick={() => setDispatchOpen(true)}>
                Print Delivery Docket (reprint)
              </Button>
              <Button variant="outline" className="w-full" onClick={() => void printJobSheetAction()}>
                Print Job Sheet
              </Button>
            </>
          )}

          <Button
            onClick={() => generateInvoice.mutate()}
            disabled={!canGenerate || generateInvoice.isPending}
            className="w-full"
            aria-label={
              canGenerate
                ? "Generate Invoice"
                : "Generate Invoice (job must be Completed)"
            }
          >
            {generateInvoice.isPending
              ? "Generating…"
              : canGenerate
              ? "Generate Invoice"
              : "Generate Invoice (job must be Completed)"}
          </Button>

          {j.generatedInvoiceId != null && (
            <Link
              to={`/invoices/${j.generatedInvoiceId}`}
              className="text-sm text-blue-600 hover:underline block"
            >
              View invoice #{j.generatedInvoiceId}
            </Link>
          )}
        </aside>

        {/* Plan III: modals */}
        <DispatchModal
          open={dispatchOpen}
          onClose={() => setDispatchOpen(false)}
          onSaved={() => {
            void qc.invalidateQueries({ queryKey: ["job", jobId] });
            void qc.invalidateQueries({ queryKey: ["job-events", jobId] });
            void qc.invalidateQueries({ queryKey: ["job-transitions", jobId] });
          }}
          jobId={j.jobId}
          jobStatus={j.currentStatusCode}
          rowVersionBase64={j.rowVersionBase64 ?? ""}
        />
        <MarkReadyConfirmModal
          open={markReadyOpen}
          onClose={() => setMarkReadyOpen(false)}
          onConfirm={() => {
            setMarkReadyOpen(false);
            changeStatus.mutate("ReadyForProduction");
          }}
          openApprovalsCount={0}
        />
      </div>
    </PageContainer>
  );
}
